/**
 * SIM-1502 — reusable revision viewer for every Dottie context entity: revisions most-recent first,
 * expandable to the full prior content. Restore is deliberately out of scope (SIM-1680 B6) and
 * renders as a disabled button with a coming-soon tooltip rather than a permanent banner, so the
 * affordance stays discoverable without implying it works.
 */
import {
  Button,
  QueryView,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@simplicity/ui";
import { useState } from "react";
import type {
  AgentFieldDefinitionRevision,
  AgentSkillRevision,
  AgentTableDefinitionRevision,
  AgentTenantConfigRevision,
  RevisionEntityType,
} from "./types";
import {
  useFieldDefRevisions,
  useSkillRevisions,
  useTableDefRevisions,
  useTenantConfigRevisions,
} from "./useDottieContext";

interface RevisionViewerProps {
  entityType: RevisionEntityType;
  /** Skill/TableDef/FieldDef id — or tenantId for tenantConfig. */
  id: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
}

interface UnifiedRevision {
  id: string;
  changedBy: string;
  reason: string | null;
  createdAt: string;
  /** Stringified prior content for display in the expanded row. */
  priorContent: string;
}

function unifySkill(r: AgentSkillRevision): UnifiedRevision {
  return {
    id: r.id,
    changedBy: r.changedBy,
    reason: r.reason,
    createdAt: r.createdAt,
    priorContent: `name: ${r.name}\n\ndescription:\n${r.description}\n\nbody:\n${r.body}`,
  };
}

function unifyTableDef(r: AgentTableDefinitionRevision): UnifiedRevision {
  return {
    id: r.id,
    changedBy: r.changedBy,
    reason: r.reason,
    createdAt: r.createdAt,
    priorContent: `tableName: ${r.tableName}\n\ndescription:\n${r.description}`,
  };
}

function unifyFieldDef(r: AgentFieldDefinitionRevision): UnifiedRevision {
  return {
    id: r.id,
    changedBy: r.changedBy,
    reason: r.reason,
    createdAt: r.createdAt,
    priorContent: `fieldName: ${r.fieldName}\n\nbody:\n${JSON.stringify(r.body, null, 2)}`,
  };
}

function unifyTenantConfig(r: AgentTenantConfigRevision): UnifiedRevision {
  return {
    id: r.id,
    changedBy: r.changedBy,
    reason: r.reason,
    createdAt: r.createdAt,
    priorContent: `additionalContext:\n${r.additionalContext ?? "(none)"}`,
  };
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString();
}

function RestoreButton() {
  return (
    <TooltipProvider delayDuration={400}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* SIM-1680: disabled buttons don't dispatch pointer events, so the
              tooltip can't anchor to them directly. Wrap in a span so the
              tooltip has a live trigger surface. No tabIndex — the button
              itself remains in DOM order for keyboard discovery via the
              adjacent View button's focus ring. */}
          <span className="inline-flex">
            <Button variant="outline" size="sm" disabled aria-label="Restore (coming soon)">
              Restore
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">Coming soon — see SIM-1167</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function RevisionRow({ revision }: { revision: UnifiedRevision }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <li className="rounded border border-border p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm">
          <div className="font-medium text-foreground">{revision.changedBy}</div>
          <div className="text-muted-foreground">{formatTimestamp(revision.createdAt)}</div>
          {revision.reason && (
            <div className="mt-1 text-muted-foreground">Reason: {revision.reason}</div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <RestoreButton />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            {expanded ? "Hide" : "View"}
          </Button>
        </div>
      </div>
      {expanded && (
        <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-xs text-foreground">
          {revision.priorContent}
        </pre>
      )}
    </li>
  );
}

function RevisionList({ revisions }: { revisions: UnifiedRevision[] }) {
  if (revisions.length === 0) {
    return <p className="text-sm text-muted-foreground">No revisions yet.</p>;
  }
  return (
    <ul className="space-y-2">
      {revisions.map((r) => (
        <RevisionRow key={r.id} revision={r} />
      ))}
    </ul>
  );
}

function SkillRevisionsBody({ id }: { id: string | null }) {
  const query = useSkillRevisions(id);
  return (
    <QueryView
      query={query}
      error={(err) => (
        <p className="text-sm text-destructive" role="alert">
          Failed to load revisions: {err.message}
        </p>
      )}
    >
      {(data) => <RevisionList revisions={data.map(unifySkill)} />}
    </QueryView>
  );
}

function TableDefRevisionsBody({ id }: { id: string | null }) {
  const query = useTableDefRevisions(id);
  return (
    <QueryView
      query={query}
      error={(err) => (
        <p className="text-sm text-destructive" role="alert">
          Failed to load revisions: {err.message}
        </p>
      )}
    >
      {(data) => <RevisionList revisions={data.map(unifyTableDef)} />}
    </QueryView>
  );
}

function FieldDefRevisionsBody({ id }: { id: string | null }) {
  const query = useFieldDefRevisions(id);
  return (
    <QueryView
      query={query}
      error={(err) => (
        <p className="text-sm text-destructive" role="alert">
          Failed to load revisions: {err.message}
        </p>
      )}
    >
      {(data) => <RevisionList revisions={data.map(unifyFieldDef)} />}
    </QueryView>
  );
}

function TenantConfigRevisionsBody({ tenantId }: { tenantId: string | null }) {
  const query = useTenantConfigRevisions(tenantId);
  return (
    <QueryView
      query={query}
      error={(err) => (
        <p className="text-sm text-destructive" role="alert">
          Failed to load revisions: {err.message}
        </p>
      )}
    >
      {(data) => <RevisionList revisions={data.map(unifyTenantConfig)} />}
    </QueryView>
  );
}

function RevisionsBody({ entityType, id }: { entityType: RevisionEntityType; id: string | null }) {
  if (entityType === "skill") return <SkillRevisionsBody id={id} />;
  if (entityType === "tableDefinition") return <TableDefRevisionsBody id={id} />;
  if (entityType === "fieldDefinition") return <FieldDefRevisionsBody id={id} />;
  return <TenantConfigRevisionsBody tenantId={id} />;
}

export function RevisionViewer({ entityType, id, open, onOpenChange, title }: RevisionViewerProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>
            Browse prior revisions of this entity. Restore is coming soon.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 overflow-y-auto pr-2">
          <RevisionsBody entityType={entityType} id={id} />
        </div>
      </SheetContent>
    </Sheet>
  );
}
